import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';

import {
  DialogCloseReason,
  DialogService,
  SuiDialogConfig,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import { SuiDialogBase } from '@sui/dialog/sui-dialog-base';
import { SuiButtonComponent } from '@sui/button';
import {
  SuiDetailItemComponent,
  SuiDetailListComponent,
} from '@sui/detail-list';
import { SuiSwitchComponent } from '@sui/switch';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  injectExecuteUserOffboardMutation,
  injectPlanUserOffboardMutation,
  UserOffboardOutcome,
} from 'src/app/api/queries/user.queries';
import {
  UserApiService,
  UserCompanyMoveRunStatus,
  UserOffboardReport,
  UserOffboardScope,
} from 'src/app/api/services/user-api.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { clearSelectedPrincipals } from 'src/app/core/state/principals.actions';
import { PickerDataobjectsDialogComponent } from 'src/app/objects/dialogs/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import { NomObjectClass, PopupType } from 'src/app/core/constants';
import { ObjectDefinition, User } from 'src/app/core/models/core.models';
import { Principal } from 'src/app/core/models/object.models';
import { AppState } from 'src/app/core/state/app.state';
import { selectUser } from 'src/app/core/state';
import { ApiErrorPresenterService } from 'src/app/core/services/api-error-presenter.service';

export interface OffboardUserDialogData {
  user: Pick<
    User,
    'id' | 'display_name' | 'email' | 'company_id' | 'company_name'
  >;
}

/** Closed with reason Save once the run executed: the report plus the re-read user. */
export type OffboardUserDialogOutput = UserOffboardOutcome;

/** The colleague who takes the objects: the signed-in user by default, or a picked principal. */
interface NewOwner {
  id: string;
  name: string;
  email?: string;
}

/** One non-zero row of the report's handover counts. */
interface HandoverRow {
  key: string;
  labelKey: string;
  active: number;
  archived: number;
  deleted: number;
  shared: number;
}

/**
 * Disable a user's account with the handover of their objects (SEN-6251,
 * WAP-2065), for a company admin from the user editor or the Users grid: pick
 * the colleague who takes the objects (the signed-in user by default), choose
 * whether every owned object moves or only the shared ones, plan (the server
 * answers with what the offboarding touches), then confirm. A planned run that
 * is not executed is cancelled on close, and a change of owner or scope after
 * the plan discards it.
 */
@Component({
  selector: 'app-offboard-user-dialog',
  templateUrl: './offboard-user-dialog.component.html',
  styleUrl: './offboard-user-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDialogContentSlot,
    SuiDialogFooterSlot,
    SuiSwitchComponent,
    SuiDetailListComponent,
    SuiDetailItemComponent,
    FormsModule,
    TranslatePipe,
  ],
})
export class OffboardUserDialogComponent
  extends SuiDialogBase<OffboardUserDialogData, OffboardUserDialogOutput>
  implements OnInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { width: 560 };
  private readonly presenter = inject(ApiErrorPresenterService);
  private readonly translate = inject(TranslateService);
  private readonly userApi = inject(UserApiService);
  private readonly utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly destroyRef = inject(DestroyRef);

  readonly planMutation = injectPlanUserOffboardMutation();
  readonly executeMutation = injectExecuteUserOffboardMutation();

  readonly newOwner = signal<NewOwner | null>(null);
  readonly sharedOnly = signal(false);
  readonly report = signal<UserOffboardReport | null>(null);
  readonly outcome = signal<UserOffboardOutcome | null>(null);

  readonly canPlan = computed(
    () => !!this.newOwner() && !this.planMutation.isPending(),
  );

  readonly canConfirm = computed(
    () => !!this.report() && !this.executeMutation.isPending(),
  );

  readonly handoverRows = computed<HandoverRow[]>(() => {
    const report = this.report();
    const counts = report?.counts ?? {};
    return Object.entries(counts)
      .filter(([, c]) => c.active + c.archived + c.deleted > 0)
      .map(([key, c]) => ({
        key,
        labelKey: key.replace(/_/g, '-'),
        active: c.active,
        archived: c.archived,
        deleted: c.deleted,
        shared: report?.shared[key] ?? 0,
      }));
  });

  readonly executed = computed(
    () =>
      this.outcome()?.report.run_status_id ===
      UserCompanyMoveRunStatus.Executed,
  );

  get user(): OffboardUserDialogData['user'] {
    return this.dialogData.user;
  }

  ngOnInit(): void {
    // the signed-in user is the default new owner, unless they are the one offboarded
    this.store
      .select(selectUser)
      .pipe(
        filter((u): u is User => !!u),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((current) => {
        if (current.id !== this.user.id) {
          this.newOwner.set({
            id: current.id,
            name: current.display_name,
            email: current.email,
          });
        }
      });
  }

  plan(): void {
    const newOwner = this.newOwner();
    if (!newOwner) {
      return;
    }
    const scope: UserOffboardScope = this.sharedOnly() ? 'shared' : 'all';
    this.clearApiError();
    this.planMutation
      .mutateAsync({ userId: this.user.id, newOwnerId: newOwner.id, scope })
      .then((report) => this.report.set(report))
      // the dialog stays on step 1 with the server's text under the form
      .catch((error: unknown) =>
        this.presenter.present(error, {
          inline: this.showApiError,
          fallback: this.translate.instant(
            'offboard-user.toast.planning-failed',
          ),
        }),
      );
  }

  setSharedOnly(sharedOnly: boolean): void {
    this.sharedOnly.set(sharedOnly);
    this.discardPlan();
  }

  chooseNewOwner(): void {
    this.store.dispatch(
      clearSelectedPrincipals({ companyId: this.user.company_id }),
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          objectClassIds: [NomObjectClass.USER],
          singleSelectionActive: true,
          useObjectMap: true,
          popupType: PopupType.SELECT_NEW_OWNER,
          // The new owner must be an enabled user of the offboarded user's
          // company - the picker would otherwise default to the signed-in
          // user's active company.
          companyId: this.user.company_id,
          lockedUserIds: [this.user.id],
        },
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
        map((event: any) => event?.data as ObjectDefinition[]),
        filter((objectDefinitions) => !!objectDefinitions?.length),
        switchMap((objectDefinitions: ObjectDefinition[]) =>
          this.utilsDataObjectApi
            .getPrincipalsByIds(objectDefinitions)
            .pipe(take(1)),
        ),
        tap((response) => {
          const principal: Principal | undefined = response?.items?.[0];
          if (principal) {
            this.newOwner.set({
              id: principal.id,
              name: principal.name ?? principal.title,
              email: principal.email,
            });
            this.discardPlan();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  confirm(): void {
    const report = this.report();
    if (!report || !this.canConfirm()) {
      return;
    }
    this.clearApiError();
    this.executeMutation
      .mutateAsync({ userId: this.user.id, runId: report.run_id })
      .then((outcome) => {
        this.outcome.set(outcome);
        const clean =
          outcome.report.run_status_id === UserCompanyMoveRunStatus.Executed &&
          (outcome.report.results?.failed ?? 0) === 0;
        if (clean) {
          this.close({ reason: DialogCloseReason.Save, data: outcome });
        }
      })
      // a 409 (run no longer pending) shows under the form; the dialog keeps
      // the plan for a retry
      .catch((error: unknown) =>
        this.presenter.present(error, {
          inline: this.showApiError,
          fallback: this.translate.instant(
            'offboard-user.toast.offboarding-failed',
          ),
        }),
      );
  }

  /** After an execute that left something to read: Save when the run went through. */
  finish(): void {
    const outcome = this.outcome();
    if (outcome && this.executed()) {
      this.close({ reason: DialogCloseReason.Save, data: outcome });
    } else {
      this.close({ reason: DialogCloseReason.Cancel });
    }
  }

  cancel(): void {
    this.discardPlan();
    this.close({ reason: DialogCloseReason.Cancel });
  }

  /** Withdraws a planned, not yet executed run; fire-and-forget, the run expires server-side anyway. */
  private discardPlan(): void {
    const report = this.report();
    if (report && !this.outcome()) {
      this.userApi
        .cancelOffboard(report.run_id)
        .pipe(catchError(() => of(null)))
        .subscribe();
      this.report.set(null);
    }
  }
}
